import React, { useEffect, useRef, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  createChart,
  CrosshairMode,
  LineStyle,
  ColorType,
} from "lightweight-charts";
import { setChartType, setActiveTimeframe } from "../../store/uiSlice";
import api from "../../services/api";
import { INSTRUMENTS } from "../../utils/constants";

const TIMEFRAMES = {
  "5M": {
    periodSeconds: 300,
    binanceInterval: "5m",
    yfInterval: "5m",
    yfPeriod: "5d",
    limit: 200,
  },
  "15M": {
    periodSeconds: 900,
    binanceInterval: "15m",
    yfInterval: "15m",
    yfPeriod: "5d",
    limit: 200,
  },
  "1H": {
    periodSeconds: 3600,
    binanceInterval: "1h",
    yfInterval: "60m",
    yfPeriod: "1mo",
    limit: 200,
  },
  "1D": {
    periodSeconds: 86400,
    binanceInterval: "1d",
    yfInterval: "1d",
    yfPeriod: "3mo",
    limit: 120,
  },
  "1W": {
    periodSeconds: 604800,
    binanceInterval: "1w",
    yfInterval: "1wk",
    yfPeriod: "1y",
    limit: 52,
  },
};

const CHART_TYPES = [
  { id: "heikin", label: "Heikin", icon: "fa-chart-column" },
  { id: "candle", label: "Candles", icon: "fa-chart-simple" },
  { id: "area", label: "Area", icon: "fa-chart-area" },
  { id: "line", label: "Line", icon: "fa-chart-line" },
  { id: "bar", label: "Bars", icon: "fa-chart-bar" },
];

const LAYOUTS = [
  { id: "1", label: "1", icon: "fa-square", cols: 1, rows: 1 },
  { id: "2h", label: "2H", icon: "fa-grip-lines", cols: 1, rows: 2 },
  { id: "2v", label: "2V", icon: "fa-grip-lines-vertical", cols: 2, rows: 1 },
  { id: "4", label: "4", icon: "fa-table-cells", cols: 2, rows: 2 },
];

const DRAW_TOOLS = [
  { id: "cursor", label: "Cursor", icon: "fa-arrow-pointer" },
  { id: "trendline", label: "Trend Line", icon: "fa-slash" },
  { id: "horizontal", label: "Horizontal", icon: "fa-minus" },
  { id: "vertical", label: "Vertical", icon: "fa-grip-lines-vertical" },
  { id: "rectangle", label: "Rectangle", icon: "fa-square" },
  { id: "fib", label: "Fibonacci", icon: "fa-wave-square" },
  { id: "text", label: "Text", icon: "fa-font" },
  { id: "eraser", label: "Eraser", icon: "fa-eraser" },
];

const DRAW_COLORS = [
  "#f59e0b",
  "#3fd1c2",
  "#f0546d",
  "#60a5fa",
  "#a78bfa",
  "#e5e7eb",
];

const INDICATOR_OPTIONS = [
  { id: "ema8", label: "EMA 8", color: "#fbbf24" },
  { id: "ema20", label: "EMA 20", color: "#22d3ee" },
  { id: "ema50", label: "EMA 50", color: "#0ea5e9" },
  { id: "ema100", label: "EMA 100", color: "#6366f1" },
  { id: "ema200", label: "EMA 200", color: "#f472b6" },
  { id: "sma20", label: "SMA 20", color: "#fb923c" },
  { id: "sma50", label: "SMA 50", color: "#a3e635" },
  { id: "rsi", label: "RSI (14)", color: "#38bdf8" },
  { id: "macd", label: "MACD (12,26,9)", color: "#c084fc" },
  { id: "lrc", label: "Linear Regression", color: "#ec4899" },
  { id: "bb", label: "Bollinger Bands", color: "#93c5fd" },
  { id: "vwap", label: "VWAP", color: "#a855f7" },
  { id: "atr", label: "ATR (14)", color: "#f97316" },
  { id: "stoch", label: "Stochastic", color: "#eab308" },
  { id: "cci", label: "CCI (20)", color: "#06b6d4" },
  { id: "adx", label: "ADX (14)", color: "#8b5cf6" },
];

const FIB_LEVELS = [0, 0.236, 0.382, 0.5, 0.618, 0.786, 1];
const FIB_COLORS = [
  "#ef4444",
  "#f97316",
  "#eab308",
  "#10b981",
  "#3b82f6",
  "#8b5cf6",
  "#ef4444",
];

const candleStore = {};
const nowUnix = () => Math.floor(Date.now() / 1000);

// Helper function to check if timeframe uses BusinessDay format
function usesBusinessDay(periodSeconds) {
  return periodSeconds >= 86400;
}

function isFiniteEpochSeconds(n) {
  return typeof n === "number" && Number.isFinite(n) && n > 0;
}

// Helper function to format time based on timeframe
function formatTimeForTimeframe(timeValue, periodSeconds) {
  if (usesBusinessDay(periodSeconds)) {
    // For daily/weekly timeframes, use BusinessDay format (YYYY-MM-DD)
    if (typeof timeValue === "string") {
      const day = timeValue.split("T")[0];
      return isNaN(Date.parse(`${day}T00:00:00Z`)) ? null : day;
    }
    if (!isFiniteEpochSeconds(timeValue)) return null;
    const d = new Date(timeValue * 1000);
    return isNaN(d.getTime()) ? null : d.toISOString().split("T")[0];
  }
  // For intraday timeframes, use Unix timestamp (seconds)
  if (typeof timeValue === "string") {
    const ms = new Date(timeValue).getTime();
    return isNaN(ms) ? null : Math.floor(ms / 1000);
  }
  return isFiniteEpochSeconds(timeValue) ? timeValue : null;
}

// Helper function to convert time to comparable Unix timestamp
function timeToUnix(timeValue, periodSeconds) {
  if (typeof timeValue === "string") {
    // For BusinessDay format
    if (usesBusinessDay(periodSeconds)) {
      const ms = new Date(timeValue + "T00:00:00Z").getTime();
      return isNaN(ms) ? 0 : Math.floor(ms / 1000);
    }
    // For intraday string timestamps
    const ms = new Date(timeValue).getTime();
    return isNaN(ms) ? 0 : Math.floor(ms / 1000);
  }
  return isFiniteEpochSeconds(timeValue) ? timeValue : 0;
}

function createSeededRandom(seed) {
  let state = seed;
  return function () {
    state = (state * 1664525 + 1013904223) % 4294967296;
    return state / 4294967296;
  };
}

// Data integrity guard
function sanitizeOhlc(data, periodSeconds) {
  const map = new Map();
  for (const c of data) {
    if (!c || c.time == null) continue;
    map.set(String(c.time), c); // last write wins on duplicate timestamps
  }
  return [...map.values()].sort(
    (a, b) => timeToUnix(a.time, periodSeconds) - timeToUnix(b.time, periodSeconds),
  );
}

// Technical Indicators Functions
function calcEMA(data, period) {
  const k = 2 / (period + 1);
  const result = [];
  let emaPrev;
  for (let i = 0; i < data.length; i++) {
    if (i === period - 1) {
      let sum = 0;
      for (let j = 0; j <= i; j++) sum += data[j].close;
      emaPrev = sum / period;
      result.push({ time: data[i].time, value: emaPrev });
    } else if (i >= period) {
      emaPrev = data[i].close * k + emaPrev * (1 - k);
      result.push({ time: data[i].time, value: emaPrev });
    }
  }
  return result;
}

function calcSMA(data, period) {
  const result = [];
  for (let i = period - 1; i < data.length; i++) {
    let sum = 0;
    for (let j = i - period + 1; j <= i; j++) sum += data[j].close;
    result.push({ time: data[i].time, value: sum / period });
  }
  return result;
}

function calcRSI(data, period = 14) {
  const result = [];
  if (data.length <= period) return result;
  let gains = 0,
    losses = 0;
  for (let i = 1; i <= period; i++) {
    const diff = data[i].close - data[i - 1].close;
    if (diff >= 0) gains += diff;
    else losses -= diff;
  }
  let avgGain = gains / period,
    avgLoss = losses / period;
  const firstRs = avgLoss === 0 ? 100 : avgGain / avgLoss;
  result.push({ time: data[period].time, value: 100 - 100 / (1 + firstRs) });
  for (let i = period + 1; i < data.length; i++) {
    const diff = data[i].close - data[i - 1].close;
    const gain = diff > 0 ? diff : 0;
    const loss = diff < 0 ? -diff : 0;
    avgGain = (avgGain * (period - 1) + gain) / period;
    avgLoss = (avgLoss * (period - 1) + loss) / period;
    const rs = avgLoss === 0 ? 100 : avgGain / avgLoss;
    result.push({ time: data[i].time, value: 100 - 100 / (1 + rs) });
  }
  return result;
}

function calcMACD(data, fastPeriod = 12, slowPeriod = 26, signalPeriod = 9) {
  const emaFast = calcEMA(data, fastPeriod);
  const emaSlow = calcEMA(data, slowPeriod);
  const macdLine = [];
  const signalLine = [];
  const histogram = [];
  
  const startIdx = slowPeriod - 1;
  for (let i = startIdx; i < data.length; i++) {
    const fastVal = emaFast.find(d => d.time === data[i].time)?.value;
    const slowVal = emaSlow.find(d => d.time === data[i].time)?.value;
    if (fastVal != null && slowVal != null) {
      macdLine.push({ time: data[i].time, value: fastVal - slowVal });
    }
  }
  
  const k = 2 / (signalPeriod + 1);
  let signalPrev;
  for (let i = 0; i < macdLine.length; i++) {
    if (i === signalPeriod - 1) {
      let sum = 0;
      for (let j = 0; j <= i; j++) sum += macdLine[j].value;
      signalPrev = sum / signalPeriod;
      signalLine.push({ time: macdLine[i].time, value: signalPrev });
    } else if (i >= signalPeriod) {
      signalPrev = macdLine[i].value * k + signalPrev * (1 - k);
      signalLine.push({ time: macdLine[i].time, value: signalPrev });
    }
    histogram.push({
      time: macdLine[i].time,
      value: macdLine[i].value - (signalLine[i]?.value || 0),
    });
  }
  
  return { macdLine, signalLine, histogram };
}

function calcLinearRegressionChannel(data, period = 20, stdDev = 1) {
  const result = { middle: [], upper: [], lower: [] };
  if (data.length < period) return result;

  for (let i = period - 1; i < data.length; i++) {
    const window = data.slice(i - period + 1, i + 1);
    let sumX = 0,
      sumY = 0,
      sumXY = 0,
      sumX2 = 0;

    for (let j = 0; j < window.length; j++) {
      const x = j;
      const y = window[j].close;
      sumX += x;
      sumY += y;
      sumXY += x * y;
      sumX2 += x * x;
    }

    const slope = (period * sumXY - sumX * sumY) / (period * sumX2 - sumX * sumX);
    const intercept = (sumY - slope * sumX) / period;
    const midValue = slope * (period - 1) + intercept;

    let sumSqDev = 0;
    for (let j = 0; j < window.length; j++) {
      const x = j;
      const y = window[j].close;
      const predicted = slope * x + intercept;
      sumSqDev += Math.pow(y - predicted, 2);
    }
    const std = Math.sqrt(sumSqDev / period) * stdDev;

    result.middle.push({ time: data[i].time, value: midValue });
    result.upper.push({ time: data[i].time, value: midValue + std });
    result.lower.push({ time: data[i].time, value: midValue - std });
  }

  return result;
}

function calcBollinger(data, period = 20, mult = 2) {
  const upper = [],
    middle = [],
    lower = [];
  for (let i = period - 1; i < data.length; i++) {
    let sum = 0;
    for (let j = i - period + 1; j <= i; j++) sum += data[j].close;
    const mean = sum / period;
    let variance = 0;
    for (let j = i - period + 1; j <= i; j++)
      variance += Math.pow(data[j].close - mean, 2);
    const sd = Math.sqrt(variance / period);
    middle.push({ time: data[i].time, value: mean });
    upper.push({ time: data[i].time, value: mean + mult * sd });
    lower.push({ time: data[i].time, value: mean - mult * sd });
  }
  return { upper, middle, lower };
}

function calcVWAP(data) {
  let cumPV = 0,
    cumVol = 0;
  return data.map((c) => {
    const typical = (c.high + c.low + c.close) / 3;
    cumPV += typical * (c.volume || 0);
    cumVol += c.volume || 0;
    return { time: c.time, value: cumVol ? cumPV / cumVol : typical };
  });
}

function calcATR(data, period = 14) {
  const result = [];
  if (data.length <= period) return result;
  
  let atrSum = 0;
  for (let i = 1; i <= period; i++) {
    const tr = Math.max(
      data[i].high - data[i].low,
      Math.abs(data[i].high - data[i - 1].close),
      Math.abs(data[i].low - data[i - 1].close)
    );
    atrSum += tr;
  }
  
  let atr = atrSum / period;
  result.push({ time: data[period].time, value: atr });
  
  for (let i = period + 1; i < data.length; i++) {
    const tr = Math.max(
      data[i].high - data[i].low,
      Math.abs(data[i].high - data[i - 1].close),
      Math.abs(data[i].low - data[i - 1].close)
    );
    atr = (atr * (period - 1) + tr) / period;
    result.push({ time: data[i].time, value: atr });
  }
  
  return result;
}

function calcStochastic(data, period = 14, smoothK = 3, smoothD = 3) {
  const result = [];
  for (let i = period - 1; i < data.length; i++) {
    let highestHigh = -Infinity;
    let lowestLow = Infinity;
    for (let j = i - period + 1; j <= i; j++) {
      highestHigh = Math.max(highestHigh, data[j].high);
      lowestLow = Math.min(lowestLow, data[j].low);
    }
    const k = ((data[i].close - lowestLow) / (highestHigh - lowestLow)) * 100;
    result.push({ time: data[i].time, k, d: 0 });
  }
  
  for (let i = smoothK - 1; i < result.length; i++) {
    let sum = 0;
    for (let j = i - smoothK + 1; j <= i; j++) sum += result[j].k;
    result[i].k = sum / smoothK;
  }
  
  for (let i = smoothK + smoothD - 2; i < result.length; i++) {
    let sum = 0;
    for (let j = i - smoothD + 1; j <= i; j++) sum += result[j].k;
    result[i].d = sum / smoothD;
  }
  
  return result;
}

function calcCCI(data, period = 20) {
  const result = [];
  for (let i = period - 1; i < data.length; i++) {
    const typicalPrices = [];
    for (let j = i - period + 1; j <= i; j++) {
      typicalPrices.push((data[j].high + data[j].low + data[j].close) / 3);
    }
    const sma = typicalPrices.reduce((a, b) => a + b, 0) / period;
    const meanDeviation = typicalPrices.reduce((a, b) => a + Math.abs(b - sma), 0) / period;
    const cci = meanDeviation === 0 ? 0 : (typicalPrices[period - 1] - sma) / (0.015 * meanDeviation);
    result.push({ time: data[i].time, value: cci });
  }
  return result;
}

function calcADX(data, period = 14) {
  const result = [];
  if (data.length < period * 2) return result;
  
  let prevDMP = 0, prevDMM = 0, prevTR = 0;
  let prevDX = 0;
  
  for (let i = 1; i < data.length; i++) {
    const high = data[i].high;
    const low = data[i].low;
    const prevHigh = data[i - 1].high;
    const prevLow = data[i - 1].low;
    const prevClose = data[i - 1].close;
    
    const upMove = high - prevHigh;
    const downMove = prevLow - low;
    
    const plusDM = upMove > downMove && upMove > 0 ? upMove : 0;
    const minusDM = downMove > upMove && downMove > 0 ? downMove : 0;
    
    const tr = Math.max(
      high - low,
      Math.abs(high - prevClose),
      Math.abs(low - prevClose)
    );
    
    if (i === 1) {
      prevDMP = plusDM;
      prevDMM = minusDM;
      prevTR = tr;
      continue;
    }
    
    prevDMP = (prevDMP * (period - 1) + plusDM) / period;
    prevDMM = (prevDMM * (period - 1) + minusDM) / period;
    prevTR = (prevTR * (period - 1) + tr) / period;
    
    const plusDI = (prevDMP / prevTR) * 100;
    const minusDI = (prevDMM / prevTR) * 100;
    const dx = (Math.abs(plusDI - minusDI) / (plusDI + minusDI)) * 100;
    
    if (i >= period * 2 - 1) {
      prevDX = i === period * 2 - 1 ? dx : (prevDX * (period - 1) + dx) / period;
      result.push({ time: data[i].time, value: prevDX });
    }
  }
  
  return result;
}

function calcHeikinAshi(data) {
  const result = [];
  for (let i = 0; i < data.length; i++) {
    if (i === 0) {
      const haClose =
        (data[i].open + data[i].high + data[i].low + data[i].close) / 4;
      const haOpen = (data[i].open + data[i].close) / 2;
      result.push({
        time: data[i].time,
        open: haOpen,
        high: data[i].high,
        low: data[i].low,
        close: haClose,
      });
    } else {
      const prev = result[i - 1];
      const haClose =
        (data[i].open + data[i].high + data[i].low + data[i].close) / 4;
      const haOpen = (prev.open + prev.close) / 2;
      result.push({
        time: data[i].time,
        open: haOpen,
        high: Math.max(data[i].high, haOpen, haClose),
        low: Math.min(data[i].low, haOpen, haClose),
        close: haClose,
      });
    }
  }
  return result;
}

function generateDemo(inst, cfg) {
  const isForex = inst.cat === "forex";
  const isCrypto = inst.cat === "crypto";
  let base = isForex
    ? 1.15
    : isCrypto
      ? 67000
      : inst.cat === "metal"
        ? 2300
        : 150;
  const dp = isForex ? 5 : 2;
  const count = cfg.limit || 200;
  const periodSeconds = cfg.periodSeconds;
  const seed =
    (inst.id || "default").split("").reduce((a, c) => a + c.charCodeAt(0), 0) +
    periodSeconds;
  const rand = createSeededRandom(seed);
  let price = base * (1 + (rand() - 0.5) * 0.01);
  const candles = [];
  const now = nowUnix();
  let trendDirection = 0,
    trendStrength = 0,
    trendRemaining = 0,
    volatilityMultiplier = 1,
    volatilityRemaining = 0;

  for (let i = 0; i < count; i++) {
    const timeValue = now - (count - 1 - i) * periodSeconds;
    
    // Format time correctly based on timeframe
    let finalTime;
    if (periodSeconds >= 86400) {
      // For daily/weekly, use BusinessDay format
      finalTime = new Date(timeValue * 1000).toISOString().split("T")[0];
    } else {
      // For intraday, use Unix timestamp
      finalTime = timeValue;
    }
    
    if (trendRemaining <= 0) {
      trendDirection = rand() < 0.5 ? -1 : 1;
      trendStrength = 0.5 + rand() * 1.0;
      trendRemaining = Math.floor(4 + rand() * 15);
    }
    trendRemaining--;
    if (volatilityRemaining <= 0) {
      volatilityMultiplier = 0.5 + rand() * 2.0;
      volatilityRemaining = Math.floor(8 + rand() * 25);
    }
    volatilityRemaining--;
    let vol =
      periodSeconds <= 300
        ? 0.00015
        : periodSeconds <= 900
          ? 0.00025
          : periodSeconds <= 3600
            ? 0.0004
            : periodSeconds <= 86400
              ? 0.0008
              : 0.0015;
    vol *= volatilityMultiplier;
    const trendMove = trendDirection * trendStrength * vol * base * 1.2;
    const noise = (rand() - 0.5) * 3 * vol * base;
    const burst = rand() < 0.05 ? (rand() - 0.5) * vol * base * 7 : 0;
    const meanReversion = (base - price) * 0.0001;
    const open = price;
    let close = price + trendMove + noise + burst + meanReversion;
    const minMove = vol * base * 0.5;
    if (Math.abs(close - open) < minMove)
      close = open + (close >= open ? minMove : -minMove);
    const bodySize = Math.abs(close - open);
    const wickPattern = rand();
    let upperWick, lowerWick;
    if (wickPattern < 0.15) {
      upperWick = bodySize * (1.5 + rand() * 4);
      lowerWick = bodySize * (0.1 + rand() * 0.5);
    } else if (wickPattern < 0.3) {
      upperWick = bodySize * (0.1 + rand() * 0.5);
      lowerWick = bodySize * (1.5 + rand() * 4);
    } else if (wickPattern < 0.35) {
      upperWick = bodySize * (2 + rand() * 3);
      lowerWick = bodySize * (2 + rand() * 3);
    } else if (wickPattern < 0.4) {
      upperWick = bodySize * 0.05;
      lowerWick = bodySize * 0.05;
    } else {
      upperWick = bodySize * (0.3 + rand() * 1.0);
      lowerWick = bodySize * (0.3 + rand() * 1.0);
    }
    const high = Math.max(open, close) + upperWick + vol * base * 0.3;
    const low = Math.min(open, close) - lowerWick - vol * base * 0.3;
    const volumeBase = 5000 + volatilityMultiplier * 15000;
    const volume = Math.floor(
      volumeBase *
        (0.5 + (bodySize / (vol * base + 0.000001)) * 3 + rand() * 0.5),
    );
    candles.push({
      time: finalTime,
      open: +open.toFixed(dp),
      high: +high.toFixed(dp),
      low: +low.toFixed(dp),
      close: +close.toFixed(dp),
      volume,
    });
    price = close;
  }
  return candles;
}

function cleanRealData(rawData, periodSeconds) {
  const map = new Map();
  for (const c of rawData) {
    if (!c) continue;
    let timeValue;
    
    if (periodSeconds >= 86400) {
      // For daily/weekly timeframes, always use BusinessDay format
      if (c.date) {
        timeValue = c.date.split("T")[0];
      } else if (c.time) {
        timeValue = new Date(c.time * 1000).toISOString().split("T")[0];
      } else {
        continue;
      }
    } else {
      // For intraday timeframes, use Unix timestamp
      timeValue = c.time || 
        (c.date ? Math.floor(new Date(c.date).getTime() / 1000) : null);
    }
    
    const open = parseFloat(c.open),
      high = parseFloat(c.high),
      low = parseFloat(c.low),
      close = parseFloat(c.close);
      
    if (!timeValue || isNaN(open) || isNaN(high) || isNaN(low) || isNaN(close))
      continue;
      
    map.set(String(timeValue), {
      time: timeValue,
      open,
      high,
      low,
      close,
      volume: parseFloat(c.volume) || 0,
    });
  }
  
  return [...map.values()].sort((a, b) => {
    const ta = typeof a.time === "string" 
      ? new Date(a.time + "T00:00:00Z").getTime() / 1000 
      : a.time;
    const tb = typeof b.time === "string" 
      ? new Date(b.time + "T00:00:00Z").getTime() / 1000 
      : b.time;
    return ta - tb;
  });
}

// ═══════════════════════════════════════════════════════
// SINGLE CHART
// ═══════════════════════════════════════════════════════

const SingleChart = ({
  instrument,
  timeframe,
  chartType,
  isLight,
  marketData,
  onSelectInstrument,
  slotIndex,
  isActive,
  onActivate,
  activeTool,
  drawColor,
  drawings,
  onAddDrawing,
  onRemoveDrawing,
  indicators,
  onToolChange,
}) => {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const mainSeriesRef = useRef(null);
  const volSeriesRef = useRef(null);
  const overlayRef = useRef(null);
  const indicatorSeriesRef = useRef({});
  const latestCandleDataRef = useRef([]);
  const fetchIdRef = useRef(0);
  const [isLoading, setIsLoading] = useState(false);
  const [candleData, setCandleData] = useState([]);
  const [candleMeta, setCandleMeta] = useState({ tf: null, id: null });
  const [showPicker, setShowPicker] = useState(false);
  const [chartVersion, setChartVersion] = useState(0);
  const isDrawingRef = useRef(false);
  const draftRef = useRef(null);
  const [forceRender, setForceRender] = useState(0);
  const [showTextInput, setShowTextInput] = useState(false);
  const [textInputValue, setTextInputValue] = useState("");
  const [textInputPosition, setTextInputPosition] = useState(null);
  const textInputRef = useRef(null);

  const CANDLE_UP = isLight ? "#10b981" : "#3fd1c2";
  const CANDLE_DOWN = isLight ? "#ef4444" : "#f0546d";

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      if (chartRef.current) chartRef.current.applyOptions({ autoSize: true });
      setForceRender((v) => v + 1);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (!showPicker) return;
    const h = (e) => {
      if (!e.target.closest("[data-picker]")) setShowPicker(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [showPicker]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isDrawingRef.current) {
        isDrawingRef.current = false;
        draftRef.current = null;
        setForceRender((v) => v + 1);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const fetchData = useCallback(async () => {
    if (!instrument) return;
    const cfg = TIMEFRAMES[timeframe] || TIMEFRAMES["1D"];
    const storeKey = `${instrument.id}_${timeframe}`;

    const requestId = ++fetchIdRef.current;
    const requestTimeframe = timeframe;
    const requestInstrumentId = instrument.id;

    // Clear cache if data has wrong time format
    if (candleStore[storeKey]?.candles?.length > 0) {
      const firstCandle = candleStore[storeKey].candles[0];
      const isBusinessDay = usesBusinessDay(cfg.periodSeconds);
      const hasWrongFormat = isBusinessDay ? typeof firstCandle.time === 'number' : typeof firstCandle.time === 'string';
      if (hasWrongFormat) {
        delete candleStore[storeKey];
      }
    }

    setIsLoading(true);
    let data = [];

    try {
      if (candleStore[storeKey]?.candles?.length > 10) {
        data = candleStore[storeKey].candles;
      } else {
        if (instrument.cat === "crypto") {
          try {
            const res = await api.get(
              `/market/binance/klines?symbol=${instrument.id}&interval=${cfg.binanceInterval}&limit=${cfg.limit}`,
            );
            if (Array.isArray(res.data) && res.data.length > 0) {
              data = cleanRealData(
                res.data.map((k) => ({
                  time: k.time || Math.floor(new Date(k.date).getTime() / 1000),
                  date: k.date,
                  open: k.open,
                  high: k.high,
                  low: k.low,
                  close: k.close,
                  volume: k.volume,
                })),
                cfg.periodSeconds,
              );
            }
          } catch (e) {}
        }

        if (!data.length) {
          try {
            const res = await api.get(
              `/market/historical?symbol=${encodeURIComponent(instrument.id)}&period=${cfg.yfPeriod}&interval=${cfg.yfInterval}`,
            );
            if (Array.isArray(res.data) && res.data.length > 0) {
              data = cleanRealData(res.data, cfg.periodSeconds);
            }
          } catch (e) {}
        }

        if (!data.length) {
          data = generateDemo(instrument, cfg);
        }

        const livePrice = marketData?.[instrument.id]?.price;
        if (livePrice && data.length) {
          const last = data[data.length - 1];
          last.close = livePrice;
          last.high = Math.max(last.high, livePrice);
          last.low = Math.min(last.low, livePrice);
        }

        data = sanitizeOhlc(
          data.map((candle) => ({
            ...candle,
            time: formatTimeForTimeframe(candle.time, cfg.periodSeconds),
          })),
          cfg.periodSeconds,
        );

        candleStore[storeKey] = { candles: data, lastUpdated: Date.now() };
      }

      data = sanitizeOhlc(
        data.map((candle) => ({
          ...candle,
          time: formatTimeForTimeframe(candle.time, cfg.periodSeconds),
        })),
        cfg.periodSeconds,
      );

      if (
        requestId !== fetchIdRef.current ||
        requestTimeframe !== timeframe ||
        requestInstrumentId !== instrument?.id
      ) {
        return;
      }

      latestCandleDataRef.current = data;
      setCandleData(data);
      setCandleMeta({ tf: requestTimeframe, id: requestInstrumentId });
      setIsLoading(false);
    } catch (e) {
      console.error("Error fetching candles:", e);
      if (requestId !== fetchIdRef.current) return;
      const fallback = sanitizeOhlc(
        generateDemo(instrument, cfg).map((candle) => ({
          ...candle,
          time: formatTimeForTimeframe(candle.time, cfg.periodSeconds),
        })),
        cfg.periodSeconds,
      );
      latestCandleDataRef.current = fallback;
      setCandleData(fallback);
      setCandleMeta({ tf: requestTimeframe, id: requestInstrumentId });
      setIsLoading(false);
    }
  }, [instrument?.id, timeframe]); // Removed marketData from dependencies

  useEffect(() => {
    setCandleData([]);
    setCandleMeta({ tf: null, id: null });
  }, [timeframe, instrument?.id]);

  useEffect(() => {
    if (instrument) {
      fetchData();
    }
  }, [fetchData, instrument?.id]);

  // Real-time price update
  useEffect(() => {
    if (!instrument || !chartRef.current || !mainSeriesRef.current) return;
    if (candleMeta.tf !== timeframe || candleMeta.id !== instrument.id) return;
    const livePrice = marketData?.[instrument.id]?.price;
    if (!livePrice) return;

    const data = latestCandleDataRef.current;
    if (!data.length) return;

    const cfg = TIMEFRAMES[timeframe] || TIMEFRAMES["1D"];
    const now = nowUnix();
    const nowBucket = Math.floor(now / cfg.periodSeconds) * cfg.periodSeconds;

    const lastCandle = data[data.length - 1];
    const lastTime = timeToUnix(lastCandle.time, cfg.periodSeconds);

    // Update existing candle
    if (lastTime === nowBucket) {
      try {
        const series = mainSeriesRef.current;
        if (!series) return;

        lastCandle.close = livePrice;
        lastCandle.high = Math.max(lastCandle.high, livePrice);
        lastCandle.low = Math.min(lastCandle.low, livePrice);

        if (
          chartType === "candle" ||
          chartType === "bar" ||
          chartType === "heikin"
        ) {
          series.update({
            time: lastCandle.time,
            open: lastCandle.open,
            high: lastCandle.high,
            low: lastCandle.low,
            close: livePrice,
          });
        } else if (chartType === "line" || chartType === "area") {
          series.update({ time: lastCandle.time, value: livePrice });
        }

        if (volSeriesRef.current) {
          volSeriesRef.current.update({
            time: lastCandle.time,
            value: lastCandle.volume || 0,
            color: livePrice >= lastCandle.open 
              ? "rgba(74, 222, 128, 0.35)"
              : "rgba(239, 68, 68, 0.35)",
          });
        }
      } catch (e) {
        console.error("Error updating candle:", e);
      }
    } 
    // Create new candle
    else if (lastTime < nowBucket) {
      try {
        const series = mainSeriesRef.current;
        if (!series) return;

        const newTime = formatTimeForTimeframe(nowBucket, cfg.periodSeconds);

        const newCandle = {
          time: newTime,
          open: livePrice,
          high: livePrice,
          low: livePrice,
          close: livePrice,
          volume: 0,
        };

        const updated = sanitizeOhlc([...data, newCandle], cfg.periodSeconds);
        latestCandleDataRef.current = updated;
        setCandleData(updated);

        if (
          chartType === "candle" ||
          chartType === "bar" ||
          chartType === "heikin"
        ) {
          series.update(newCandle);
        } else if (chartType === "line" || chartType === "area") {
          series.update({ time: newTime, value: livePrice });
        }

        if (volSeriesRef.current) {
          volSeriesRef.current.update({
            time: newTime,
            value: 0,
            color: "rgba(100,116,139,0.5)",
          });
        }
      } catch (e) {
        console.error("Error creating new candle:", e);
      }
    }
  }, [
    marketData?.[instrument?.id]?.price,
    timeframe,
    instrument?.id,
    chartType,
    candleMeta,
  ]);

  // Chart creation
  useEffect(() => {
    if (!candleData.length || !containerRef.current) return;
    if (candleMeta.tf !== timeframe || candleMeta.id !== instrument?.id) return;

    const cfg = TIMEFRAMES[timeframe] || TIMEFRAMES["1D"];

    const normalizedData = sanitizeOhlc(
      candleData.map((candle) => ({
        ...candle,
        time: formatTimeForTimeframe(candle.time, cfg.periodSeconds),
      })),
      cfg.periodSeconds,
    );

    if (chartRef.current) {
      try {
        chartRef.current.remove();
      } catch (_) {}
      chartRef.current = null;
      mainSeriesRef.current = null;
      volSeriesRef.current = null;
      indicatorSeriesRef.current = {};
    }

    const chartBg = isLight ? "#ffffff" : "#131722";
    const chartGrid = isLight ? "rgba(0,0,0,0.04)" : "rgba(255,255,255,0.05)";
    const chartText = isLight ? "#454545" : "#d1d5db";
    const chartBorder = isLight ? "rgba(0,0,0,0.10)" : "rgba(255,255,255,0.08)";

    const chart = createChart(containerRef.current, {
      autoSize: true,
      layout: {
        background: { type: ColorType.Solid, color: chartBg },
        textColor: chartText,
        fontSize: 11,
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif",
        attributionUrl: "",
      },
      grid: {
        vertLines: { color: chartGrid, style: 0 },
        horzLines: { color: chartGrid, style: 0 },
      },
      crosshair: { 
        mode: CrosshairMode.Normal,
        vertLine: {
          color: chartBorder,
          width: 1,
          style: 0,
        },
        horzLine: {
          color: chartBorder,
          width: 1,
          style: 0,
        },
      },
      rightPriceScale: {
        borderColor: chartBorder,
        autoScale: true,
        scaleMargins: { top: 0.05, bottom: 0.05 },
        fontSize: 10,
        textColor: chartText,
      },
      leftPriceScale: {
        visible: false,
      },
      timeScale: {
        borderColor: chartBorder,
        timeVisible: true,
        secondsVisible: false,
        barSpacing: 7,
        minBarSpacing: 2,
        fontSize: 10,
        textColor: chartText,
      },
      handleScroll: { mouseWheel: true, pressedMouseMove: true },
      handleScale: {
        axisPressedMouseMove: true,
        mouseWheel: true,
        pinch: true,
      },
    });
    chartRef.current = chart;

    let displayData = normalizedData;
    if (chartType === "heikin") displayData = calcHeikinAshi(normalizedData);

    if (chartType === "line") {
      mainSeriesRef.current = chart.addLineSeries({
        color: "#cbd5e1",
        lineWidth: 2,
        lastValueVisible: true,
      });
      mainSeriesRef.current.setData(
        displayData.map((c) => ({ time: c.time, value: c.close })),
      );
    } else if (chartType === "area") {
      mainSeriesRef.current = chart.addAreaSeries({
        lineColor: "#cbd5e1",
        topColor: "rgba(203,213,225,0.12)",
        bottomColor: "rgba(203,213,225,0.01)",
        lastValueVisible: true,
      });
      mainSeriesRef.current.setData(
        displayData.map((c) => ({ time: c.time, value: c.close })),
      );
    } else if (chartType === "bar") {
      mainSeriesRef.current = chart.addBarSeries({
        upColor: CANDLE_UP,
        downColor: CANDLE_DOWN,
        lastValueVisible: true,
      });
      mainSeriesRef.current.setData(displayData);
    } else {
      // Default to candlestick (handles both "candle" and "heikin")
      mainSeriesRef.current = chart.addCandlestickSeries({
        upColor: CANDLE_UP,
        downColor: CANDLE_DOWN,
        borderUpColor: CANDLE_UP,
        borderDownColor: CANDLE_DOWN,
        wickUpColor: CANDLE_UP,
        wickDownColor: CANDLE_DOWN,
        lastValueVisible: true,
        priceLineVisible: true,
        priceLineWidth: 1,
        priceLineStyle: LineStyle.Dashed,
      });
      mainSeriesRef.current.priceScale().applyOptions({
        autoScale: true,
        scaleMargins: { top: 0.05, bottom: 0.15 },
      });
      mainSeriesRef.current.setData(displayData);
    }

    volSeriesRef.current = chart.addHistogramSeries({
      priceScaleId: "vol",
      priceFormat: { type: "volume" },
      lastValueVisible: false,
      priceLineVisible: false,
      color: "rgba(100,116,139,0.5)",
    });
    volSeriesRef.current.priceScale().applyOptions({ 
      scaleMargins: { top: 0.85, bottom: 0 },
      autoScale: true,
    });
    volSeriesRef.current.setData(
      normalizedData.map((c) => ({
        time: c.time,
        value: c.volume || 0,
        color: c.close >= c.open 
          ? "rgba(74, 222, 128, 0.35)"
          : "rgba(239, 68, 68, 0.35)",
      })),
    );

    chart.timeScale().fitContent();
    setChartVersion((v) => v + 1);

    return () => {
      if (chartRef.current) {
        try {
          chartRef.current.remove();
        } catch (_) {}
        chartRef.current = null;
      }
    };
  }, [candleData, chartType, isLight, instrument?.cat, instrument?.id, timeframe, candleMeta]);

  // Apply Indicators
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || !candleData.length) return;
    if (candleMeta.tf !== timeframe || candleMeta.id !== instrument?.id) return;

    const cfg = TIMEFRAMES[timeframe] || TIMEFRAMES["1D"];
    const normalizedData = sanitizeOhlc(
      candleData.map((candle) => ({
        ...candle,
        time: formatTimeForTimeframe(candle.time, cfg.periodSeconds),
      })),
      cfg.periodSeconds,
    );

    Object.values(indicatorSeriesRef.current).forEach((s) => {
      (Array.isArray(s) ? s : [s]).forEach((ser) => {
        try {
          chart.removeSeries(ser);
        } catch (_) {}
      });
    });
    indicatorSeriesRef.current = {};

    (indicators || []).forEach((ind) => {
      switch (ind) {
        case "ema8": {
          const s = chart.addLineSeries({
            color: "#fbbf24",
            lineWidth: 2,
            priceLineVisible: false,
            lastValueVisible: true,
            crosshairMarkerVisible: true,
            crosshairMarkerRadius: 4,
          });
          s.setData(calcEMA(normalizedData, 8));
          indicatorSeriesRef.current.ema8 = s;
          break;
        }
        case "ema20": {
          const s = chart.addLineSeries({
            color: "#22d3ee",
            lineWidth: 2,
            priceLineVisible: false,
            lastValueVisible: true,
            crosshairMarkerVisible: true,
            crosshairMarkerRadius: 4,
          });
          s.setData(calcEMA(normalizedData, 20));
          indicatorSeriesRef.current.ema20 = s;
          break;
        }
        case "ema50": {
          const s = chart.addLineSeries({
            color: "#0ea5e9",
            lineWidth: 2,
            priceLineVisible: false,
            lastValueVisible: true,
            crosshairMarkerVisible: true,
            crosshairMarkerRadius: 4,
          });
          s.setData(calcEMA(normalizedData, 50));
          indicatorSeriesRef.current.ema50 = s;
          break;
        }
        case "ema100": {
          const s = chart.addLineSeries({
            color: "#6366f1",
            lineWidth: 2,
            priceLineVisible: false,
            lastValueVisible: true,
            crosshairMarkerVisible: true,
            crosshairMarkerRadius: 4,
          });
          s.setData(calcEMA(normalizedData, 100));
          indicatorSeriesRef.current.ema100 = s;
          break;
        }
        case "ema200": {
          const s = chart.addLineSeries({
            color: "#f472b6",
            lineWidth: 2,
            priceLineVisible: false,
            lastValueVisible: true,
            crosshairMarkerVisible: true,
            crosshairMarkerRadius: 4,
          });
          s.setData(calcEMA(normalizedData, 200));
          indicatorSeriesRef.current.ema200 = s;
          break;
        }
        case "sma20": {
          const s = chart.addLineSeries({
            color: "#fb923c",
            lineWidth: 2,
            priceLineVisible: false,
            lastValueVisible: true,
          });
          s.setData(calcSMA(normalizedData, 20));
          indicatorSeriesRef.current.sma20 = s;
          break;
        }
        case "sma50": {
          const s = chart.addLineSeries({
            color: "#a3e635",
            lineWidth: 2,
            priceLineVisible: false,
            lastValueVisible: true,
          });
          s.setData(calcSMA(normalizedData, 50));
          indicatorSeriesRef.current.sma50 = s;
          break;
        }
        case "rsi": {
          const rsiData = calcRSI(normalizedData, 14);
          
          const rsiLine = chart.addLineSeries({
            color: "#38bdf8",
            lineWidth: 2,
            priceScaleId: "rsi",
            priceLineVisible: false,
            lastValueVisible: true,
            crosshairMarkerVisible: true,
            crosshairMarkerRadius: 4,
          });
          rsiLine.priceScale().applyOptions({
            scaleMargins: { top: 0.75, bottom: 0.05 },
            autoScale: true,
          });
          rsiLine.setData(rsiData);

          const rsiVolume = chart.addHistogramSeries({
            priceScaleId: "rsi",
            priceFormat: { type: "volume" },
            lastValueVisible: false,
            priceLineVisible: false,
          });
          rsiVolume.priceScale().applyOptions({
            scaleMargins: { top: 0.75, bottom: 0.05 },
            autoScale: true,
          });
          
          const volumeData = normalizedData.map((c, i) => {
            const rsiValue = rsiData.find(d => d.time === c.time)?.value || 50;
            const volumeScale = Math.min(c.volume / 10000, 100);
            return {
              time: c.time,
              value: Math.max(0, rsiValue - volumeScale),
              color: c.close >= c.open 
                ? "rgba(56, 189, 248, 0.4)" 
                : "rgba(56, 189, 248, 0.2)"
            };
          });
          rsiVolume.setData(volumeData);

          indicatorSeriesRef.current.rsi = [rsiVolume, rsiLine];
          break;
        }
        case "macd": {
          const macdData = calcMACD(normalizedData);
          
          const macdLine = chart.addLineSeries({
            color: "#c084fc",
            lineWidth: 2,
            priceScaleId: "macd",
            priceLineVisible: false,
            lastValueVisible: true,
          });
          macdLine.priceScale().applyOptions({
            scaleMargins: { top: 0.75, bottom: 0.05 },
          });
          macdLine.setData(macdData.macdLine);

          const signalLine = chart.addLineSeries({
            color: "#fbbf24",
            lineWidth: 1.5,
            priceScaleId: "macd",
            priceLineVisible: false,
            lastValueVisible: true,
          });
          signalLine.priceScale().applyOptions({
            scaleMargins: { top: 0.75, bottom: 0.05 },
          });
          signalLine.setData(macdData.signalLine);

          const histogram = chart.addHistogramSeries({
            priceScaleId: "macd",
            priceFormat: { type: "volume" },
            lastValueVisible: false,
            priceLineVisible: false,
          });
          histogram.priceScale().applyOptions({
            scaleMargins: { top: 0.75, bottom: 0.05 },
          });
          histogram.setData(macdData.histogram.map(h => ({
            ...h,
            color: h.value >= 0 ? "rgba(192, 132, 252, 0.5)" : "rgba(239, 68, 68, 0.5)"
          })));

          indicatorSeriesRef.current.macd = [histogram, macdLine, signalLine];
          break;
        }
        case "lrc": {
          const lrc = calcLinearRegressionChannel(normalizedData, 20, 1);
          
          const mid = chart.addLineSeries({
            color: "#ec4899",
            lineWidth: 2.5,
            priceLineVisible: false,
            lastValueVisible: true,
            crosshairMarkerVisible: true,
            crosshairMarkerRadius: 4,
          });
          mid.setData(lrc.middle);

          const up = chart.addLineSeries({
            color: "rgba(236, 72, 153, 0.5)",
            lineWidth: 1.5,
            lineStyle: LineStyle.Dashed,
            priceLineVisible: false,
            lastValueVisible: false,
          });
          up.setData(lrc.upper);

          const low = chart.addLineSeries({
            color: "rgba(236, 72, 153, 0.5)",
            lineWidth: 1.5,
            lineStyle: LineStyle.Dashed,
            priceLineVisible: false,
            lastValueVisible: false,
          });
          low.setData(lrc.lower);

          indicatorSeriesRef.current.lrc = [mid, up, low];
          break;
        }
        case "bb": {
          const bb = calcBollinger(normalizedData, 20, 2);
          const up = chart.addLineSeries({
            color: "rgba(147,197,253,0.7)",
            lineWidth: 1,
            priceLineVisible: false,
            lastValueVisible: false,
          });
          up.setData(bb.upper);
          const mid = chart.addLineSeries({
            color: "rgba(147,197,253,0.4)",
            lineWidth: 1,
            lineStyle: LineStyle.Dotted,
            priceLineVisible: false,
            lastValueVisible: false,
          });
          mid.setData(bb.middle);
          const low = chart.addLineSeries({
            color: "rgba(147,197,253,0.7)",
            lineWidth: 1,
            priceLineVisible: false,
            lastValueVisible: false,
          });
          low.setData(bb.lower);
          indicatorSeriesRef.current.bb = [up, mid, low];
          break;
        }
        case "vwap": {
          const s = chart.addLineSeries({
            color: "#a855f7",
            lineWidth: 1.5,
            priceLineVisible: false,
            lastValueVisible: false,
          });
          s.setData(calcVWAP(normalizedData));
          indicatorSeriesRef.current.vwap = s;
          break;
        }
        case "atr": {
          const atrData = calcATR(normalizedData, 14);
          const s = chart.addLineSeries({
            color: "#f97316",
            lineWidth: 2,
            priceScaleId: "atr",
            priceLineVisible: false,
            lastValueVisible: true,
          });
          s.priceScale().applyOptions({
            scaleMargins: { top: 0.85, bottom: 0.02 },
          });
          s.setData(atrData);
          indicatorSeriesRef.current.atr = s;
          break;
        }
        case "stoch": {
          const stochData = calcStochastic(normalizedData);
          
          const kLine = chart.addLineSeries({
            color: "#eab308",
            lineWidth: 2,
            priceScaleId: "stoch",
            priceLineVisible: false,
            lastValueVisible: true,
          });
          kLine.priceScale().applyOptions({
            scaleMargins: { top: 0.75, bottom: 0.05 },
          });
          kLine.setData(stochData.map(d => ({ time: d.time, value: d.k })));

          const dLine = chart.addLineSeries({
            color: "#06b6d4",
            lineWidth: 1.5,
            priceScaleId: "stoch",
            priceLineVisible: false,
            lastValueVisible: true,
          });
          dLine.priceScale().applyOptions({
            scaleMargins: { top: 0.75, bottom: 0.05 },
          });
          dLine.setData(stochData.map(d => ({ time: d.time, value: d.d })));

          indicatorSeriesRef.current.stoch = [kLine, dLine];
          break;
        }
        case "cci": {
          const cciData = calcCCI(normalizedData, 20);
          const s = chart.addLineSeries({
            color: "#06b6d4",
            lineWidth: 2,
            priceScaleId: "cci",
            priceLineVisible: false,
            lastValueVisible: true,
          });
          s.priceScale().applyOptions({
            scaleMargins: { top: 0.75, bottom: 0.05 },
          });
          s.setData(cciData);
          indicatorSeriesRef.current.cci = s;
          break;
        }
        case "adx": {
          const adxData = calcADX(normalizedData, 14);
          const s = chart.addLineSeries({
            color: "#8b5cf6",
            lineWidth: 2,
            priceScaleId: "adx",
            priceLineVisible: false,
            lastValueVisible: true,
          });
          s.priceScale().applyOptions({
            scaleMargins: { top: 0.85, bottom: 0.02 },
          });
          s.setData(adxData);
          indicatorSeriesRef.current.adx = s;
          break;
        }
      }
    });
  }, [indicators, candleData, chartVersion, timeframe, candleMeta, instrument?.id]);

  // Drawing overlay RENDER
  useEffect(() => {
    const canvas = overlayRef.current;
    const chart = chartRef.current;
    const series = mainSeriesRef.current;
    if (!canvas || !chart || !series) return;
    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, rect.width, rect.height);
    ctx.font = "9px monospace";
    ctx.lineWidth = 1.5;

    const toXY = (pt) => {
      const x = chart.timeScale().timeToCoordinate(pt.time);
      const y = series.priceToCoordinate(pt.price);
      return x == null || y == null ? null : { x, y };
    };

    const allDrawings = draftRef.current
      ? [...drawings, draftRef.current]
      : drawings;

    allDrawings.forEach((d) => {
      ctx.strokeStyle = d.color || drawColor;
      ctx.fillStyle = d.color || drawColor;

      if (d.type === "trendline") {
        if (d.points.length < 2) return;
        const c1 = toXY(d.points[0]);
        const c2 = toXY(d.points[1]);
        if (!c1 || !c2) return;
        
        const isDraft = d.id === "draft";
        
        ctx.save();
        
        if (isDraft) {
          ctx.lineWidth = 1.5;
          ctx.setLineDash([5, 5]);
          ctx.globalAlpha = 0.7;
          
          ctx.strokeStyle = "rgba(0, 0, 0, 0.3)";
          ctx.beginPath();
          ctx.moveTo(c1.x + 1, c1.y + 1);
          ctx.lineTo(c2.x + 1, c2.y + 1);
          ctx.stroke();
          
          ctx.lineCap = "round";
          ctx.lineJoin = "round";
          ctx.strokeStyle = d.color || drawColor;
          ctx.beginPath();
          ctx.moveTo(c1.x, c1.y);
          ctx.lineTo(c2.x, c2.y);
          ctx.stroke();
          
          ctx.setLineDash([]);
          ctx.fillStyle = d.color || drawColor;
          ctx.beginPath();
          ctx.arc(c1.x, c1.y, 4, 0, Math.PI * 2);
          ctx.fill();
          ctx.strokeStyle = "#ffffff";
          ctx.lineWidth = 1.5;
          ctx.stroke();
          
          ctx.fillStyle = d.color || drawColor;
          ctx.globalAlpha = 0.5;
          ctx.beginPath();
          ctx.arc(c2.x, c2.y, 3, 0, Math.PI * 2);
          ctx.fill();
          ctx.strokeStyle = "#ffffff";
          ctx.lineWidth = 1;
          ctx.stroke();
        } else {
          ctx.lineWidth = 2.5;
          ctx.setLineDash([]);
          ctx.globalAlpha = 1;
          
          ctx.strokeStyle = "rgba(0, 0, 0, 0.3)";
          ctx.beginPath();
          ctx.moveTo(c1.x + 1, c1.y + 1);
          ctx.lineTo(c2.x + 1, c2.y + 1);
          ctx.stroke();
          
          ctx.lineCap = "round";
          ctx.lineJoin = "round";
          ctx.strokeStyle = d.color || drawColor;
          ctx.beginPath();
          ctx.moveTo(c1.x, c1.y);
          ctx.lineTo(c2.x, c2.y);
          ctx.stroke();
          
          ctx.setLineDash([]);
          ctx.fillStyle = d.color || drawColor;
          ctx.globalAlpha = 0.8;
          
          ctx.beginPath();
          ctx.arc(c1.x, c1.y, 4, 0, Math.PI * 2);
          ctx.fill();
          ctx.strokeStyle = "#ffffff";
          ctx.lineWidth = 1.5;
          ctx.stroke();
          
          ctx.beginPath();
          ctx.arc(c2.x, c2.y, 4, 0, Math.PI * 2);
          ctx.fill();
          ctx.strokeStyle = "#ffffff";
          ctx.lineWidth = 1.5;
          ctx.stroke();
        }
        
        ctx.restore();
        ctx.globalAlpha = 1;
        ctx.setLineDash([]);
      } else if (d.type === "horizontal") {
        const y = series.priceToCoordinate(d.points[0].price);
        if (y == null) return;
        ctx.lineWidth = 1.5;
        ctx.setLineDash([4, 4]);
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(rect.width, y);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.fillText(d.points[0].price.toFixed(5), rect.width - 80, y - 4);
      } else if (d.type === "vertical") {
        const x = chart.timeScale().timeToCoordinate(d.points[0].time);
        if (x == null) return;
        ctx.lineWidth = 1.5;
        ctx.setLineDash([4, 4]);
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, rect.height);
        ctx.stroke();
        ctx.setLineDash([]);
      } else if (d.type === "rectangle") {
        if (d.points.length < 2) return;
        const c1 = toXY(d.points[0]);
        const c2 = toXY(d.points[1]);
        if (!c1 || !c2) return;
        const x = Math.min(c1.x, c2.x),
          y = Math.min(c1.y, c2.y);
        const w = Math.abs(c2.x - c1.x),
          h = Math.abs(c2.y - c1.y);
        
        const isDraft = d.id === "draft";
        ctx.globalAlpha = isDraft ? 0.1 : 0.15;
        ctx.fillRect(x, y, w, h);
        ctx.globalAlpha = isDraft ? 0.5 : 1;
        ctx.lineWidth = isDraft ? 1.5 : 2;
        if (isDraft) ctx.setLineDash([5, 5]);
        ctx.strokeRect(x, y, w, h);
        ctx.setLineDash([]);
        ctx.globalAlpha = 1;
      } else if (d.type === "fib") {
        if (d.points.length < 2) return;
        const c1 = toXY(d.points[0]);
        const c2 = toXY(d.points[1]);
        if (!c1 || !c2) return;
        const p1 = d.points[0].price,
          p2 = d.points[1].price;
        const xStart = Math.min(c1.x, c2.x),
          xEnd = Math.max(c1.x, c2.x);
        FIB_LEVELS.forEach((lvl, idx) => {
          const price = p1 + (p2 - p1) * lvl;
          const y = series.priceToCoordinate(price);
          if (y == null) return;
          ctx.strokeStyle = FIB_COLORS[idx];
          ctx.fillStyle = FIB_COLORS[idx];
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(xStart, y);
          ctx.lineTo(xEnd, y);
          ctx.stroke();
          ctx.fillText(
            `${(lvl * 100).toFixed(1)}% ${price.toFixed(5)}`,
            xEnd + 4,
            y - 2,
          );
        });
      } else if (d.type === "text") {
        const c1 = toXY(d.points[0]);
        if (!c1) return;
        ctx.fillText(d.text || "", c1.x, c1.y);
      }
    });
  }, [drawings, drawColor, chartVersion, forceRender]);

  // Mouse handling
  const getTimePrice = (e) => {
    const rect = overlayRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left,
      y = e.clientY - rect.top;
    if (!chartRef.current || !mainSeriesRef.current) return null;
    const time = chartRef.current.timeScale().coordinateToTime(x);
    const price = mainSeriesRef.current.coordinateToPrice(y);
    if (time == null || price == null) return null;
    return { time, price, x, y };
  };

  const handleTextSubmit = () => {
    if (textInputValue.trim() && textInputPosition) {
      onAddDrawing({
        id: Date.now() + Math.random(),
        type: "text",
        points: [
          { time: textInputPosition.time, price: textInputPosition.price },
        ],
        text: textInputValue.trim(),
        color: drawColor,
      });
    }
    setShowTextInput(false);
    setTextInputValue("");
    setTextInputPosition(null);
    if (onToolChange) onToolChange("cursor");
  };

  const handleOverlayMouseDown = (e) => {
    if (!isActive || activeTool === "cursor") return;
    e.stopPropagation();
    const tp = getTimePrice(e);
    if (!tp) return;

    if (activeTool === "eraser") {
      let bestIdx = -1,
        bestDist = 12;
      drawings.forEach((d, idx) => {
        const chart = chartRef.current,
          series = mainSeriesRef.current;
        if (!chart || !series) return;
        if (d.type === "horizontal") {
          const py = series.priceToCoordinate(d.points[0].price);
          if (py != null) {
            const dist = Math.abs(tp.y - py);
            if (dist < bestDist) {
              bestDist = dist;
              bestIdx = idx;
            }
          }
        } else if (d.type === "vertical") {
          const px = chart.timeScale().timeToCoordinate(d.points[0].time);
          if (px != null) {
            const dist = Math.abs(tp.x - px);
            if (dist < bestDist) {
              bestDist = dist;
              bestIdx = idx;
            }
          }
        } else if (d.type === "text") {
          const px = chart.timeScale().timeToCoordinate(d.points[0].time);
          const py = series.priceToCoordinate(d.points[0].price);
          if (px != null && py != null) {
            const dist = Math.hypot(tp.x - px, tp.y - py);
            if (dist < bestDist) {
              bestDist = dist;
              bestIdx = idx;
            }
          }
        } else if (d.type === "trendline" || d.type === "rectangle" || d.type === "fib") {
          if (d.points.length >= 2) {
            for (let i = 0; i < d.points.length - 1; i++) {
              const p1 = {
                x: chart.timeScale().timeToCoordinate(d.points[i].time),
                y: series.priceToCoordinate(d.points[i].price)
              };
              const p2 = {
                x: chart.timeScale().timeToCoordinate(d.points[i + 1].time),
                y: series.priceToCoordinate(d.points[i + 1].price)
              };
              
              if (p1.x == null || p1.y == null || p2.x == null || p2.y == null) continue;
              
              const A = tp.x - p1.x;
              const B = tp.y - p1.y;
              const C = p2.x - p1.x;
              const D = p2.y - p1.y;
              
              const dot = A * C + B * D;
              const lenSq = C * C + D * D;
              let param = -1;
              if (lenSq != 0) param = dot / lenSq;
              
              let xx, yy;
              if (param < 0) {
                xx = p1.x;
                yy = p1.y;
              } else if (param > 1) {
                xx = p2.x;
                yy = p2.y;
              } else {
                xx = p1.x + param * C;
                yy = p1.y + param * D;
              }
              
              const dist = Math.hypot(tp.x - xx, tp.y - yy);
              if (dist < bestDist) {
                bestDist = dist;
                bestIdx = idx;
              }
            }
          }
        }
      });
      if (bestIdx >= 0) onRemoveDrawing(bestIdx);
      return;
    }

    if (activeTool === "text") {
      setTextInputPosition({
        time: tp.time,
        price: tp.price,
        x: tp.x,
        y: tp.y,
      });
      setTextInputValue("");
      setShowTextInput(true);
      setTimeout(() => {
        if (textInputRef.current) textInputRef.current.focus();
      }, 50);
      return;
    }

    if (activeTool === "horizontal" || activeTool === "vertical") {
      onAddDrawing({
        id: Date.now() + Math.random(),
        type: activeTool,
        points: [{ time: tp.time, price: tp.price }],
        color: drawColor,
      });
      return;
    }

    if (activeTool === "trendline" || activeTool === "rectangle" || activeTool === "fib") {
      if (!isDrawingRef.current) {
        isDrawingRef.current = true;
        draftRef.current = {
          id: "draft",
          type: activeTool,
          points: [
            { time: tp.time, price: tp.price },
            { time: tp.time, price: tp.price },
          ],
          color: drawColor,
        };
        setForceRender((v) => v + 1);
      } else {
        const startPoint = draftRef.current?.points?.[0];
        if (startPoint) {
          const isDifferentPoint = 
            startPoint.time !== tp.time || 
            startPoint.price !== tp.price;
          
          if (isDifferentPoint) {
            onAddDrawing({
              id: Date.now() + Math.random(),
              type: activeTool,
              points: [
                startPoint,
                { time: tp.time, price: tp.price }
              ],
              color: drawColor,
            });
          }
          
          isDrawingRef.current = false;
          draftRef.current = null;
          setForceRender((v) => v + 1);
        }
      }
    }
  };

  const handleOverlayMouseMove = (e) => {
    if (!isDrawingRef.current || !draftRef.current) return;
    
    if (draftRef.current.type !== "trendline" && 
        draftRef.current.type !== "rectangle" && 
        draftRef.current.type !== "fib") return;
    
    const tp = getTimePrice(e);
    if (!tp) return;
    
    draftRef.current = {
      ...draftRef.current,
      points: [
        draftRef.current.points[0],
        { time: tp.time, price: tp.price }
      ],
    };
    
    setForceRender((v) => v + 1);
  };

  const handleOverlayMouseUp = (e) => {
    // Don't finalize on mouse up for two-click drawing
  };

  const overlayInteractive = isActive && activeTool && activeTool !== "cursor";

  return (
    <div
      onMouseDown={() => onActivate(slotIndex)}
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        boxSizing: "border-box",
        border: isActive
          ? "2px solid var(--color-orange, #f59e0b)"
          : "1px solid transparent",
      }}
    >
      <div
        data-picker
        style={{
          display: "flex",
          alignItems: "center",
          gap: 4,
          padding: "2px 5px",
          background: "var(--color-bgSurface)",
          borderBottom: "1px solid var(--color-borderMedium)",
          flexShrink: 0,
          position: "relative",
          zIndex: 10,
        }}
      >
        <button
          onClick={() => {
            onActivate(slotIndex);
            setShowPicker((v) => !v);
          }}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            padding: "2px 6px",
            fontSize: 9,
            fontFamily: "monospace",
            cursor: "pointer",
            borderRadius: 3,
            border: "none",
            background: "transparent",
            color: "var(--color-text)",
          }}
        >
          <i className="fas fa-chevron-down" style={{ fontSize: 7 }} />
          {instrument?.id || "Select Instrument"}
        </button>
        <span
          style={{
            fontSize: 7,
            fontFamily: "monospace",
            color: "var(--color-textDim)",
          }}
        >
          {timeframe}
        </span>
        {isActive && (
          <span
            style={{
              fontSize: 7,
              fontFamily: "monospace",
              color: "var(--color-orange, #f59e0b)",
              marginLeft: "auto",
            }}
          >
            ACTIVE
          </span>
        )}

        {showPicker && (
          <div
            style={{
              position: "absolute",
              left: 5,
              top: "100%",
              zIndex: 9999,
              background: "var(--color-bgSurface)",
              border: "1px solid var(--color-borderHover)",
              borderRadius: 6,
              minWidth: 140,
              maxHeight: 250,
              overflowY: "auto",
              boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
            }}
          >
            {INSTRUMENTS.map((inst) => (
              <button
                key={inst.id}
                onClick={() => {
                  onSelectInstrument(slotIndex, inst);
                  setShowPicker(false);
                }}
                style={{
                  width: "100%",
                  padding: "5px 10px",
                  fontSize: 9,
                  fontFamily: "monospace",
                  cursor: "pointer",
                  textAlign: "left",
                  background:
                    instrument?.id === inst.id
                      ? "var(--color-orangeBg)"
                      : "transparent",
                  color:
                    instrument?.id === inst.id
                      ? "var(--color-text)"
                      : "var(--color-textSecondary)",
                  border: "none",
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                }}
              >
                <span
                  style={{
                    fontSize: 7,
                    color: "var(--color-textDim)",
                    width: 15,
                  }}
                >
                  {inst.cat.slice(0, 3).toUpperCase()}
                </span>
                <span style={{ flex: 1 }}>{inst.id}</span>
                <span style={{ fontSize: 7, color: "var(--color-textDim)" }}>
                  {inst.name?.split(" ").slice(0, 2).join(" ")}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div style={{ flex: 1, position: "relative", minHeight: 0 }}>
        {isLoading && (
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%,-50%)",
              zIndex: 10,
            }}
          >
            <div
              style={{
                width: 18,
                height: 18,
                border: "2px solid var(--color-orange)",
                borderTopColor: "transparent",
                borderRadius: "50%",
                animation: "spin .8s linear infinite",
              }}
            />
          </div>
        )}
        {instrument ? (
          <>
            <div ref={containerRef} style={{ width: "100%", height: "100%" }} />
            <canvas
              ref={overlayRef}
              onMouseDown={handleOverlayMouseDown}
              onMouseMove={handleOverlayMouseMove}
              onMouseUp={handleOverlayMouseUp}
              onMouseLeave={handleOverlayMouseUp}
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                pointerEvents: overlayInteractive ? "auto" : "none",
                cursor: overlayInteractive
                  ? activeTool === "eraser"
                    ? "not-allowed"
                    : "crosshair"
                  : "default",
                zIndex: 5,
              }}
            />
            {showTextInput && textInputPosition && (
              <div
                style={{
                  position: "absolute",
                  left: Math.min(
                    Math.max(textInputPosition.x, 10),
                    (overlayRef.current?.getBoundingClientRect().width || 200) -
                      220,
                  ),
                  top: Math.min(
                    Math.max(textInputPosition.y, 10),
                    (overlayRef.current?.getBoundingClientRect().height ||
                      200) - 120,
                  ),
                  zIndex: 100,
                  background: "#ffffff",
                  border: "2px solid #8b5cf6",
                  borderRadius: 12,
                  padding: "12px",
                  boxShadow: "0 8px 30px rgba(139, 92, 246, 0.25)",
                  width: 220,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    marginBottom: 8,
                  }}
                >
                  <div
                    style={{
                      width: 20,
                      height: 20,
                      borderRadius: 6,
                      background: "rgba(139, 92, 246, 0.12)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <i
                      className="fas fa-font"
                      style={{ fontSize: 10, color: "#8b5cf6" }}
                    />
                  </div>
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      fontFamily: "monospace",
                      color: "#1f2937",
                    }}
                  >
                    Add Annotation
                  </span>
                  <button
                    onClick={() => {
                      setShowTextInput(false);
                      setTextInputValue("");
                      setTextInputPosition(null);
                      if (onToolChange) onToolChange("cursor");
                    }}
                    style={{
                      marginLeft: "auto",
                      width: 20,
                      height: 20,
                      borderRadius: 6,
                      border: "none",
                      background: "#f3f4f6",
                      color: "#6b7280",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <i className="fas fa-times" style={{ fontSize: 8 }} />
                  </button>
                </div>
                <input
                  ref={textInputRef}
                  type="text"
                  value={textInputValue}
                  onChange={(e) => setTextInputValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleTextSubmit();
                    if (e.key === "Escape") {
                      setShowTextInput(false);
                      setTextInputValue("");
                      setTextInputPosition(null);
                      if (onToolChange) onToolChange("cursor");
                    }
                  }}
                  placeholder="Enter text..."
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    borderRadius: 8,
                    border: "1px solid #e5e7eb",
                    fontSize: 12,
                    outline: "none",
                    color: "#1f2937",
                    background: "#ffffff",
                    boxSizing: "border-box",
                    fontFamily: "monospace",
                  }}
                />
                <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                  <button
                    onClick={handleTextSubmit}
                    style={{
                      flex: 1,
                      padding: "8px",
                      borderRadius: 8,
                      border: "none",
                      background: "linear-gradient(to right, #8b5cf6, #a78bfa)",
                      color: "#ffffff",
                      fontSize: 10,
                      fontWeight: 700,
                      fontFamily: "monospace",
                      cursor: "pointer",
                    }}
                  >
                    <i
                      className="fas fa-check"
                      style={{ marginRight: 4, fontSize: 8 }}
                    />{" "}
                    ADD
                  </button>
                  <button
                    onClick={() => {
                      setShowTextInput(false);
                      setTextInputValue("");
                      setTextInputPosition(null);
                      if (onToolChange) onToolChange("cursor");
                    }}
                    style={{
                      flex: 1,
                      padding: "8px",
                      borderRadius: 8,
                      border: "1px solid #e5e7eb",
                      background: "#f9fafb",
                      color: "#6b7280",
                      fontSize: 10,
                      fontWeight: 700,
                      fontFamily: "monospace",
                      cursor: "pointer",
                    }}
                  >
                    CANCEL
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              gap: 8,
            }}
          >
            <button
              onClick={() => {
                onActivate(slotIndex);
                setShowPicker(true);
              }}
              style={{
                padding: "8px 16px",
                fontSize: 10,
                fontFamily: "monospace",
                cursor: "pointer",
                borderRadius: 6,
                border: "1px dashed var(--color-borderHover)",
                background: "var(--color-bgSurface)",
                color: "var(--color-textDim)",
              }}
            >
              <i className="fas fa-plus" style={{ marginRight: 5 }} />
              Select Instrument
            </button>
          </div>
        )}
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
};

// ═══════════════════════════════════════════════════════
// MAIN CHART
// ═══════════════════════════════════════════════════════

const Chart = ({ instrument }) => {
  const dispatch = useDispatch();
  const { chartType, activeTimeframe } = useSelector((s) => s.ui);
  const { marketData } = useSelector((s) => s.trade);
  const theme = useSelector((s) => s.ui.theme);
  const isLight = theme === "light";

  const [layout, setLayout] = useState("1");
  const [chartInstruments, setChartInstruments] = useState([
    null,
    null,
    null,
    null,
  ]);
  const [activeSlot, setActiveSlot] = useState(0);
  const [openMenu, setOpenMenu] = useState(null);
  const [chartExpanded, setChartExpanded] = useState(false);
  const [activeTool, setActiveTool] = useState("cursor");
  const [drawColor, setDrawColor] = useState(DRAW_COLORS[0]);
  const [drawingsBySlot, setDrawingsBySlot] = useState({
    0: [],
    1: [],
    2: [],
    3: [],
  });
  const [indicatorsBySlot, setIndicatorsBySlot] = useState({
    0: [],
    1: [],
    2: [],
    3: [],
  });

  // Set default chart type to "heikin" if not already set
  useEffect(() => {
    if (!chartType) {
      dispatch(setChartType("heikin"));
    }
  }, []);

  useEffect(() => {
    if (!activeTimeframe || activeTimeframe === "1D") {
      const timer = setTimeout(() => {
        dispatch(setActiveTimeframe("15M"));
      }, 0);
      return () => clearTimeout(timer);
    }
  }, []);

  const layoutConfig = LAYOUTS.find((l) => l.id === layout) || LAYOUTS[0];
  const { cols, rows } = layoutConfig;
  const totalCharts = cols * rows;
  const visibleInstruments = chartInstruments.slice(0, totalCharts);
  while (visibleInstruments.length < totalCharts) visibleInstruments.push(null);

  useEffect(() => {
    if (activeSlot >= totalCharts) setActiveSlot(0);
  }, [totalCharts, activeSlot]);

  useEffect(() => {
    if (!instrument) return;
    setChartInstruments((prev) => {
      if (prev[activeSlot]?.id === instrument.id) return prev;
      const next = [...prev];
      next[activeSlot] = instrument;
      return next;
    });
  }, [instrument?.id]);

  const handleSelectInstrument = (slotIndex, inst) => {
    setChartInstruments((prev) => {
      const next = [...prev];
      next[slotIndex] = inst;
      return next;
    });
    setActiveSlot(slotIndex);
  };

  const handleToggleExpand = () => {
    setChartExpanded((prev) => {
      const newState = !prev;
      window.dispatchEvent(
        new CustomEvent("toggle-chart-expand", { detail: newState }),
      );
      return newState;
    });
  };

  const handleAddDrawing = (slotIndex, drawing) => {
    setDrawingsBySlot((prev) => ({
      ...prev,
      [slotIndex]: [...(prev[slotIndex] || []), drawing],
    }));
  };

  const handleRemoveDrawing = (slotIndex, idx) => {
    setDrawingsBySlot((prev) => ({
      ...prev,
      [slotIndex]: (prev[slotIndex] || []).filter((_, i) => i !== idx),
    }));
  };

  const handleClearDrawings = () => {
    setDrawingsBySlot((prev) => ({ ...prev, [activeSlot]: [] }));
  };

  const toggleIndicator = (indId) => {
    setIndicatorsBySlot((prev) => {
      const current = prev[activeSlot] || [];
      const next = current.includes(indId)
        ? current.filter((i) => i !== indId)
        : [...current, indId];
      return { ...prev, [activeSlot]: next };
    });
  };

  const activeIndicators = indicatorsBySlot[activeSlot] || [];

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Top Toolbar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 3,
          padding: "3px 6px",
          background: "var(--color-bgSurface)",
          borderBottom: "1px solid var(--color-borderMedium)",
          flexShrink: 0,
          flexWrap: "wrap",
        }}
      >
        <div style={{ position: "relative" }}>
          <button
            onClick={() => setOpenMenu((v) => (v === "type" ? null : "type"))}
            style={{
              padding: "4px 10px",
              fontSize: 9,
              fontFamily: "monospace",
              cursor: "pointer",
              borderRadius: 4,
              border: "1px solid var(--color-borderMedium)",
              background: "var(--color-bgCard)",
              color: "var(--color-text)",
              display: "flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            <i
              className={`fas ${CHART_TYPES.find((t) => t.id === chartType)?.icon}`}
              style={{ fontSize: 9 }}
            />
            {CHART_TYPES.find((t) => t.id === chartType)?.label}
            <i className="fas fa-chevron-down" style={{ fontSize: 7 }} />
          </button>
          {openMenu === "type" && (
            <div
              style={{
                position: "absolute",
                left: 0,
                top: "100%",
                zIndex: 9999,
                background: "var(--color-bgSurface)",
                border: "1px solid var(--color-borderHover)",
                borderRadius: 6,
                minWidth: 130,
                boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
              }}
            >
              {CHART_TYPES.map((ct) => (
                <button
                  key={ct.id}
                  onClick={() => {
                    dispatch(setChartType(ct.id));
                    setOpenMenu(null);
                  }}
                  style={{
                    width: "100%",
                    padding: "6px 12px",
                    fontSize: 10,
                    fontFamily: "monospace",
                    cursor: "pointer",
                    textAlign: "left",
                    background:
                      chartType === ct.id
                        ? "var(--color-orangeBg)"
                        : "transparent",
                    color:
                      chartType === ct.id
                        ? "var(--color-text)"
                        : "var(--color-textSecondary)",
                    border: "none",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <i className={`fas ${ct.icon}`} style={{ fontSize: 9 }} />
                  {ct.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <div style={{ position: "relative" }}>
          <button
            onClick={() =>
              setOpenMenu((v) => (v === "indicators" ? null : "indicators"))
            }
            style={{
              padding: "4px 10px",
              fontSize: 9,
              fontFamily: "monospace",
              cursor: "pointer",
              borderRadius: 4,
              border: "1px solid var(--color-borderMedium)",
              background: "var(--color-bgCard)",
              color: "var(--color-text)",
              display: "flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            <i className="fas fa-wave-square" style={{ fontSize: 9 }} />
            Indicators{" "}
            {activeIndicators.length > 0 && `(${activeIndicators.length})`}
            <i className="fas fa-chevron-down" style={{ fontSize: 7 }} />
          </button>
          {openMenu === "indicators" && (
            <div
              style={{
                position: "absolute",
                left: 0,
                top: "100%",
                zIndex: 9999,
                background: "var(--color-bgSurface)",
                border: "1px solid var(--color-borderHover)",
                borderRadius: 6,
                minWidth: 200,
                boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
                maxHeight: 400,
                overflowY: "auto",
              }}
            >
              {INDICATOR_OPTIONS.map((opt) => (
                <button
                  key={opt.id}
                  onClick={() => toggleIndicator(opt.id)}
                  style={{
                    width: "100%",
                    padding: "6px 12px",
                    fontSize: 10,
                    fontFamily: "monospace",
                    cursor: "pointer",
                    textAlign: "left",
                    background: "transparent",
                    color: activeIndicators.includes(opt.id)
                      ? opt.color
                      : "var(--color-textSecondary)",
                    border: "none",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <i
                    className={`fas ${activeIndicators.includes(opt.id) ? "fa-check-square" : "fa-square"}`}
                    style={{ fontSize: 9, color: opt.color }}
                  />
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <span
          style={{
            fontSize: 8,
            fontFamily: "monospace",
            color: "var(--color-textDim)",
            marginLeft: 4,
          }}
        >
          LAYOUT:
        </span>
        {LAYOUTS.map((l) => (
          <button
            key={l.id}
            onClick={() => setLayout(l.id)}
            style={{
              padding: "4px 8px",
              fontSize: 9,
              fontFamily: "monospace",
              cursor: "pointer",
              borderRadius: 4,
              border: "1px solid var(--color-borderMedium)",
              background:
                layout === l.id
                  ? "var(--color-orangeBg)"
                  : "var(--color-bgCard)",
              color:
                layout === l.id ? "var(--color-text)" : "var(--color-textDim)",
            }}
          >
            <i className={`fas ${l.icon}`} style={{ fontSize: 8 }} />
            {l.label}
          </button>
        ))}

        <button
          onClick={handleToggleExpand}
          style={{
            padding: "4px 10px",
            fontSize: 9,
            fontFamily: "monospace",
            cursor: "pointer",
            borderRadius: 4,
            border: "1px solid var(--color-borderMedium)",
            background: chartExpanded
              ? "var(--color-orangeBg)"
              : "var(--color-bgCard)",
            color: chartExpanded ? "var(--color-text)" : "var(--color-textDim)",
            marginLeft: "auto",
          }}
        >
          <i
            className={`fas ${chartExpanded ? "fa-compress" : "fa-expand"}`}
            style={{ fontSize: 9 }}
          />
        </button>
      </div>

      {/* Body with drawing toolbar */}
      <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
            padding: "6px 3px",
            background: "var(--color-bgSurface)",
            borderRight: "1px solid var(--color-borderMedium)",
            flexShrink: 0,
          }}
        >
          {DRAW_TOOLS.map((tool) => (
            <button
              key={tool.id}
              title={tool.label}
              onClick={() => setActiveTool(tool.id)}
              style={{
                width: 26,
                height: 26,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 4,
                border: "none",
                cursor: "pointer",
                background:
                  activeTool === tool.id
                    ? "var(--color-orangeBg)"
                    : "transparent",
                color:
                  activeTool === tool.id
                    ? "var(--color-text)"
                    : "var(--color-textDim)",
              }}
            >
              <i className={`fas ${tool.icon}`} style={{ fontSize: 10 }} />
            </button>
          ))}
          <div
            style={{
              width: "70%",
              height: 1,
              background: "var(--color-borderMedium)",
              margin: "4px 0",
            }}
          />
          {DRAW_COLORS.map((c) => (
            <button
              key={c}
              title={c}
              onClick={() => setDrawColor(c)}
              style={{
                width: 16,
                height: 16,
                borderRadius: "50%",
                cursor: "pointer",
                background: c,
                border:
                  drawColor === c
                    ? "2px solid var(--color-text)"
                    : "1px solid rgba(0,0,0,0.2)",
              }}
            />
          ))}
          <div
            style={{
              width: "70%",
              height: 1,
              background: "var(--color-borderMedium)",
              margin: "4px 0",
            }}
          />
          <button
            title="Clear all drawings"
            onClick={handleClearDrawings}
            style={{
              width: 26,
              height: 26,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 4,
              border: "none",
              cursor: "pointer",
              background: "transparent",
              color: "var(--color-red)",
            }}
          >
            <i className="fas fa-trash" style={{ fontSize: 10 }} />
          </button>
        </div>

        {/* Charts Grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${cols}, 1fr)`,
            gridTemplateRows: `repeat(${rows}, 1fr)`,
            gap: 1,
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          {visibleInstruments.map((inst, idx) => (
            <div
              key={idx}
              style={{
                position: "relative",
                minHeight: 0,
                overflow: "hidden",
                border: "1px solid var(--color-borderMedium)",
                background: "var(--color-bg)",
              }}
            >
              <SingleChart
                instrument={inst}
                timeframe={activeTimeframe}
                chartType={chartType}
                isLight={isLight}
                marketData={marketData}
                onSelectInstrument={handleSelectInstrument}
                slotIndex={idx}
                isActive={activeSlot === idx}
                onActivate={setActiveSlot}
                activeTool={activeTool}
                drawColor={drawColor}
                drawings={drawingsBySlot[idx] || []}
                onAddDrawing={(d) => handleAddDrawing(idx, d)}
                onRemoveDrawing={(i) => handleRemoveDrawing(idx, i)}
                indicators={indicatorsBySlot[idx] || []}
                onToolChange={setActiveTool}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Chart;